import { ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react';
import PosSelect from './PosSelect';
import { POS_PRODUCT_SORTS, POS_SORT_DESC, POS_SORT_BY_CUSTOM } from '../constants/posConst';
import '../css/Pos.css';

/**
 * A sort field and a direction toggle, as one control.
 *
 * Kept out of the screen that uses it so the till and Inventory can order their
 * grids the same way without either owning the markup. It holds no state of its
 * own — the screen does, because the screen is what has to apply the ordering
 * and what decides whether a field is even worth offering.
 *
 * The field itself is a PosSelect rather than a native `<select>`: the options
 * in a native one are drawn by the operating system, so they cannot be padded
 * or themed and the current choice cannot be marked. See that file.
 *
 * THE DIRECTION BUTTON NAMES THE END IT WILL TAKE YOU TO, in the words of the
 * field it applies to: on Price it reads "Dearest first", on Items sold "Best
 * sellers". An arrow labelled "descending" is accurate and tells a person
 * standing at a counter nothing — descending WHAT, and is that the end they
 * want?
 *
 * @param {object} props
 * @param {string} props.sortKey - the field in force, a POS_PRODUCT_SORTS key
 * @param {string} props.direction - POS_SORT_ASC or POS_SORT_DESC
 * @param {(key: string) => void} props.onSortChange - called with the new
 *   field. The caller is responsible for resetting the direction to that
 *   field's `defaultDirection`; see useSavedSort's changeSort.
 * @param {() => void} props.onDirectionToggle
 * @param {Array<{key: string, label: string, ascLabel: string, descLabel: string}>} [props.options]
 *   - the fields to offer, defaulting to all of them. The till passes a
 *   narrowed list when a field cannot be answered.
 * @param {() => void} [props.onSaveCustom] - saves the hand-arranged order.
 *   Given only to someone allowed to save one; without it Custom still shows
 *   the saved order, but offers no Save button.
 * @param {boolean} [props.customDirty] - there are unsaved drags
 * @param {boolean} [props.customSaving] - a save is in flight
 * @returns {JSX.Element}
 */
function PosSortBar({
  sortKey,
  direction,
  onSortChange,
  onDirectionToggle,
  options = POS_PRODUCT_SORTS,
  onSaveCustom,
  customDirty = false,
  customSaving = false,
}) {
  const active = options.find((option) => option.key === sortKey) || options[0];
  const ascending = direction !== POS_SORT_DESC;
  // What pressing the button will DO, not what is in force — it is the label on
  // a control, so it has to describe the action rather than the state.
  const nextLabel = ascending ? active?.descLabel : active?.ascLabel;
  // An arrangement has no "other end", so Custom trades the direction button
  // for the Save that commits the arrangement.
  const isCustom = active?.key === POS_SORT_BY_CUSTOM;

  return (
    <div className="pos-sortbar">
      <PosSelect
        className="pos-sortbar-select"
        label="Sort by"
        options={options}
        value={active?.key}
        onChange={onSortChange}
      />

      {/* `title` as well as `aria-label`: the button is icon-only, so a mouse
          user has no other way to find out which end it takes them to. */}
      {isCustom ? (onSaveCustom && (
        <button
          type="button"
          className="pos-btn pos-btn-primary pos-sortbar-save"
          onClick={onSaveCustom}
          disabled={!customDirty || customSaving}
          title={customDirty ? 'Save this arrangement for everyone' : 'Drag the cards to arrange them, then save'}
        >
          {customSaving ? 'Saving…' : 'Save'}
        </button>
      )) : (
      <button
        type="button"
        className="pos-sortbar-dir"
        onClick={onDirectionToggle}
        aria-label={nextLabel}
        title={nextLabel}
      >
        {ascending
          ? <ArrowUpNarrowWide size={16} aria-hidden="true" />
          : <ArrowDownWideNarrow size={16} aria-hidden="true" />}
      </button>
      )}
    </div>
  );
}

export default PosSortBar;
